import { useEffect, useRef, useState } from "react";
import { Terminal as XTerm } from "xterm";
import { FitAddon } from "xterm-addon-fit";
import "xterm/css/xterm.css";
import { Terminal as TerminalIcon, Wifi, WifiOff, Maximize2, Minimize2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Interactive investigation terminal — proxies stdin/stdout to either a
 * backend-local PTY (mode='local') or to the bridge daemon (mode='bridge').
 *
 * Props:
 *  - cardId     (required)
 *  - mode       ("local" | "bridge")
 *  - context    optional markdown investigation context
 *  - token      JWT (for WS auth via query)
 *  - apiBase    REACT_APP_BACKEND_URL
 *  - onClose    optional
 */
export default function InteractiveTerminal({ cardId, mode, context = "", token, apiBase, onClose }) {
  const containerRef = useRef(null);
  const termRef = useRef(null);
  const fitRef = useRef(null);
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [status, setStatus] = useState("starting");
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    if (!containerRef.current || termRef.current) return;
    let disposed = false;

    const term = new XTerm({
      cursorBlink: true,
      convertEol: true,
      fontFamily: "'JetBrains Mono', 'Fira Code', ui-monospace, monospace",
      fontSize: 12,
      lineHeight: 1.35,
      scrollback: 4000,
      theme: {
        background: "#0a0a0a",
        foreground: "#e5e5e5",
        cursor: "#e5e5e5",
        selectionBackground: "#3b3b3b",
      },
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(containerRef.current);
    fitRef.current = fit;
    termRef.current = term;
    try { fit.fit(); } catch {/* renderer may not be ready */}

    term.writeln(`\x1b[90m// ratchet investigation · mode=${mode}\x1b[0m`);

    const path = mode === "bridge" ? "/terminal/bridge/sessions" : "/terminal/local/sessions";
    api.post(path, { card_id: String(cardId), context }).then((res) => {
      if (disposed) return;
      const { session_id, ws_path } = res.data;
      const base = (apiBase || "").replace(/^http/, "ws");
      const wsUrl = `${base}${ws_path}?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        setStatus("connected");
        try {
          ws.send(JSON.stringify({ type: "resize", cols: term.cols, rows: term.rows }));
        } catch {/* ignore */}
      };
      ws.onmessage = (e) => {
        let msg;
        try { msg = JSON.parse(e.data); } catch { return; }
        if (msg.type === "output") {
          term.write(msg.data);
        } else if (msg.type === "session_opened") {
          term.writeln(`\x1b[32m// session opened (${session_id.slice(0, 8)})\x1b[0m`);
        } else if (msg.type === "session_failed") {
          term.writeln(`\x1b[31m// session failed: ${msg.error || "unknown"}\x1b[0m`);
          setStatus("failed");
          setErrorMsg(msg.error);
        } else if (msg.type === "exited") {
          term.writeln(`\r\n\x1b[33m// terminated (exit ${msg.exit_code})\x1b[0m\r\n`);
          setConnected(false);
          setStatus("exited");
        } else if (msg.type === "error") {
          term.writeln(`\r\n\x1b[31m// error: ${msg.message}\x1b[0m\r\n`);
        }
      };
      ws.onclose = () => {
        setConnected(false);
        if (status !== "exited" && status !== "failed") setStatus("closed");
      };
      ws.onerror = () => { setErrorMsg("WebSocket connection error"); };

      term.onData((data) => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "input", data }));
        }
      });
      term.onResize(({ cols, rows }) => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "resize", cols, rows }));
        }
      });
    }).catch((err) => {
      const detail = err?.response?.data?.detail || err?.message || "Failed to create session";
      term.writeln(`\x1b[31m// failed to start: ${detail}\x1b[0m`);
      setStatus("failed");
      setErrorMsg(detail);
    });

    const onWindowResize = () => { try { fit.fit(); } catch {/* */} };
    window.addEventListener("resize", onWindowResize);

    return () => {
      disposed = true;
      window.removeEventListener("resize", onWindowResize);
      try { wsRef.current?.close(); } catch {/* */}
      try { term.dispose(); } catch {/* */}
      termRef.current = null;
    };
  }, [cardId, mode, context, token, apiBase]);

  useEffect(() => {
    const t = setTimeout(() => { try { fitRef.current?.fit(); } catch {/* */} }, 60);
    return () => clearTimeout(t);
  }, [maximized]);

  return (
    <div
      className={`flex flex-col bg-[#0a0a0a] border border-white/10 rounded-sm overflow-hidden ${maximized ? "fixed inset-4 z-50" : "h-[480px]"}`}
      data-testid="interactive-terminal"
    >
      <div className="flex items-center justify-between px-3 py-1.5 bg-white/5 border-b border-white/10">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-white/60">
          <TerminalIcon className="w-3 h-3" />
          terminal · {mode}
          {connected ? <Wifi className="w-3 h-3 text-emerald-400" /> : <WifiOff className="w-3 h-3 text-white/30" />}
          <span className="text-white/30">· {status}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setMaximized((v) => !v)}
            className="p-1 text-white/50 hover:text-white"
            title={maximized ? "Restore" : "Maximize"}
          >
            {maximized ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
          </button>
        </div>
      </div>
      <div ref={containerRef} className="flex-1 p-1" />
      {errorMsg && (
        <div className="px-3 py-1 text-[10px] font-mono text-red-400 border-t border-white/10 bg-red-950/20">
          {errorMsg}
        </div>
      )}
    </div>
  );
}
